/**
 * Campaign React Query hooks
 * Following the same pattern as useTournamentsQuery.ts
 */

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCampaigns,
  fetchCampaignPrizes,
  fetchCampaignWins,
  joinCampaign
} from 'core/api/endpoints/campaigns.api';

import type {
  ICampaignSessionData,
  IJoinCampaignProps,
  IJoinCampaignResponse
} from 'core/api/endpoints/campaigns.api';

import {
  PRIZE_POP_CAMPAIGN_TYPE,
  REBATE_CAMPAIGN_TYPE,
  type CampaignsResponse,
  type ICampaignPrizesResponse,
  type ICampaignWinsResponse,
  type CampaignTypes,
  type CampaignSource
} from '../../types/campaign.types';

import { useDemo, useIsIdle, useSettings } from '../../../../store/app/appStore';
import { useCampaignsStore, useCampaignActions } from '../../store/campaignsStore';
import { campaignConfigRegistry } from '../../configs';
import { ensureMockRebateCampaigns } from '../../mocks/seedMockRebateCampaigns';
import { isLocalEnvironment } from '../../../../core/utils/environment';

export const resolveCampaignConfig = (campaignType: CampaignTypes) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const settings = useSettings();

  const config = campaignConfigRegistry[campaignType];
  const host = (settings as unknown as Record<string, unknown>)[config.hostSettingsKey] as string;
  const assetsHost = settings.assetsHost || '';
  const sessionInfo: ICampaignSessionData = {
    sessionKey: settings.sessionKey || '',
    playerId: settings.playerId || ''
  };

  return { config, host, assetsHost, sessionInfo };
};

// Query keys factory
export const campaignQueryKeys = {
  all: ['campaigns'] as const,
  lists: () => [...campaignQueryKeys.all, 'list'] as const,
  list: (type: CampaignTypes, host: string, sessionInfo: ICampaignSessionData) =>
    [...campaignQueryKeys.lists(), { type, host, sessionInfo }] as const,
  prizes: () => [...campaignQueryKeys.all, 'prizes'] as const,
  prize: (host: string, campaignId: string) => 
    [...campaignQueryKeys.prizes(), { host, campaignId }] as const,
  wins: () => [...campaignQueryKeys.all, 'wins'] as const,
  win: (host: string, campaignId: string) => 
    [...campaignQueryKeys.wins(), { host, campaignId }] as const,
};

/**
 * Hook for fetching campaigns list
 * Automatically resolves config and settings based on campaign type
 */
export const useCampaignsQuery = (campaignType: CampaignTypes, options?: { enabled?: boolean }) => {
  const isIdle = useIsIdle();
  const isDemoStore = useDemo();
  // QA mocking master switch — when on, suppress every real campaigns refetch
  // so the synthetic list is stable. Gate at the hook source, not at each
  // call site (mirrors the tournaments precedent).
  const mockMode = useCampaignsStore((state) => state.mockMode);
  const { setCampaignsFromResponse, setCampaignType } = useCampaignActions();

  const { config, host, assetsHost, sessionInfo } = resolveCampaignConfig(campaignType);
  const campaignOnlyType = useSettings().campaignOnly?.type;
  const { timing, retry } = config.mainFetch;
  const hasValidSettings = Boolean(host && sessionInfo.sessionKey && sessionInfo.playerId);
  const isDemo = process.env['NODE_ENV'] === 'development'
    ? new URLSearchParams(window.location.search).get('demo') === 'true'
    : isDemoStore;

  const query = useQuery<CampaignsResponse, Error>({
    queryKey: campaignQueryKeys.list(campaignType, host, sessionInfo),
    queryFn: () => fetchCampaigns(config, host, assetsHost, sessionInfo),
    enabled: (options?.enabled !== false) && !isIdle && !isDemo && hasValidSettings && !mockMode,
    refetchInterval: timing.refetchInterval,
    refetchIntervalInBackground: timing.refetchIntervalInBackground,
    refetchOnWindowFocus: timing.refetchOnWindowFocus,
    staleTime: timing.staleTime,
    gcTime: 5 * 60 * 1000,
    retry: retry.maxRetries,
    retryDelay: (attemptIndex) => Math.min(retry.retryDelayBase * 2 ** attemptIndex, retry.maxRetryDelay),
  });

  useEffect(() => {
    if (query.data?.campaigns && query.data.campaigns.length > 0) {
      // In ingame campaign-only mode only the opened type owns the global
      // campaignType. Don't let a background list query for the other type flip
      // it — that flip makes type-derived lookups (getSelectedCampaign in
      // PrizesTab, panel info, etc.) intermittently resolve the wrong campaign.
      if (!campaignOnlyType || campaignOnlyType === campaignType) {
        setCampaignType(campaignType);
      }
      setCampaignsFromResponse(query.data, campaignType);
    }
  }, [query.data, campaignType, setCampaignsFromResponse, setCampaignType, campaignOnlyType]);

  // TODO_Rebate: Remove this once the BE is ready and the rebate campaigns
  // are returned by the real endpoint. This keeps mock rebate campaigns
  // present alongside whatever the (not-yet-ready) real backend returns.
  useEffect(() => {
    if (!isLocalEnvironment()) return;
    if (campaignType !== REBATE_CAMPAIGN_TYPE) return;

    void ensureMockRebateCampaigns();
  }, [campaignType, query.data, query.isError]);

  return query;
};

/**
 * Fetches prizes for a specific campaign. Used by cards to poll only when visible.
 */
export const useCampaignPrizesQueryById = (campaign: CampaignSource | null | undefined, isVisible: boolean) => {
  const isIdle = useIsIdle();
  // QA mocking master switch — see useCampaignPrizesQuery.
  const mockMode = useCampaignsStore((state) => state.mockMode);

  // Resolve config unconditionally (it calls useSettings) so the hook order is
  // stable across renders where `campaign` toggles defined/undefined. A safe
  // fallback type keeps the registry lookup from throwing when campaign is null;
  // correctness is driven purely by the query's `enabled` flag below.
  const { config, host, sessionInfo } = resolveCampaignConfig(campaign?.type ?? PRIZE_POP_CAMPAIGN_TYPE);
  const prizesFetch = config.subFetches['prizes'];

  return useQuery<ICampaignPrizesResponse, Error>({
    queryKey: campaign
      ? campaignQueryKeys.prize(host, campaign.id)
      : ['campaigns', 'prizes', 'empty'],
    queryFn: () => campaign
      ? fetchCampaignPrizes({ config, host, campaignId: campaign.id, sessionInfo })
      : Promise.reject('No campaign'),
    enabled: !!campaign && !!campaign.type && !isIdle && isVisible && !!prizesFetch && !!host && !mockMode,
    staleTime: prizesFetch?.timing.staleTime || 30000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: prizesFetch?.timing.refetchInterval,
    refetchOnWindowFocus: prizesFetch?.timing.refetchOnWindowFocus,
    retry: prizesFetch?.retry.maxRetries || 2,
    retryDelay: prizesFetch ? (attemptIndex) => Math.min(prizesFetch.retry.retryDelayBase * 2 ** attemptIndex, prizesFetch.retry.maxRetryDelay) : undefined,
  });
};

/**
 * Hook for fetching campaign wins
 * Config-driven: only runs if the selected campaign's config has a 'wins' sub-fetch
 */
export const useCampaignWinsQuery = () => {
  const isIdle = useIsIdle();
  // QA mocking master switch — hits the real wins endpoint with the
  // selected campaign id; a `qa-mock-…` id would 404-spam.
  const mockMode = useCampaignsStore((state) => state.mockMode);
  const selectedCampaign = useCampaignsStore((state) => state.getSelectedCampaign());
  const campaignType = selectedCampaign?.type as CampaignTypes | undefined;
  const { config, host, sessionInfo } = resolveCampaignConfig(
    campaignType || PRIZE_POP_CAMPAIGN_TYPE
  );
  const winsFetch = config.subFetches['wins'];
  const hasWins = !!selectedCampaign && !!winsFetch;

  return useQuery<ICampaignWinsResponse, Error>({
    queryKey: hasWins
      ? campaignQueryKeys.win(host, selectedCampaign.id)
      : ['campaigns', 'wins', 'empty'],
    queryFn: () => hasWins
      ? fetchCampaignWins({ config, host, campaignId: selectedCampaign.id, sessionInfo })
      : Promise.reject('No campaign selected'),
    enabled: !isIdle && hasWins && !!host && !mockMode,
    staleTime: winsFetch?.timing.staleTime || 30000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: winsFetch?.timing.refetchInterval,
    refetchOnWindowFocus: winsFetch?.timing.refetchOnWindowFocus,
    retry: winsFetch?.retry.maxRetries || 2,
    retryDelay: winsFetch ? (attemptIndex) => Math.min(winsFetch.retry.retryDelayBase * 2 ** attemptIndex, winsFetch.retry.maxRetryDelay) : undefined,
  });
};

/**
 * Hook for joining campaigns
 */
export const useJoinCampaignMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: IJoinCampaignProps) => joinCampaign(params),
    onSuccess: (data: IJoinCampaignResponse[], _variables: IJoinCampaignProps) => {
      // Invalidate campaigns list to refresh joined status
      queryClient.invalidateQueries({
        queryKey: campaignQueryKeys.lists()
      });
      
      // Invalidate prizes and wins for joined campaigns
      const anyJoined = data.some(result => result.joined);

      if (anyJoined) {
        queryClient.invalidateQueries({
          queryKey: campaignQueryKeys.prizes()
        });
        queryClient.invalidateQueries({
          queryKey: campaignQueryKeys.wins()
        });
      }
    },
    retry: 1,
    retryDelay: 1000,
  });
};
